import { fromJS, List } from 'immutable';
import {
  LOAD_BOARD_TASKS_SUCCESS,
  LOAD_BOARD_TASKS,
  RESET_BOARD_TASKS,
  DELETE_TASK_SUCCESS,
  APPEND_ACTIVITY_LOG,
  UPDATE_TASK_ORDER_SUCCESS,
  UPDATE_TASK,
  RESET_TASK_DRAG,
  UPDATE_TASK_SUCCESS,
} from 'constants/ActionTypes';
import { CLOSE_TYPE, LABEL_TYPE, UPDATE_TYPE } from 'constants/NotificationConstants';
import { TASK_SPECIAL_STATUS_IDS, TASK_STATUS_FINISHED } from 'helpers/taskHelpers';

const initialState = fromJS({
  projectId: null,
  pagination: {},
  datasets: {},
});

const getTaskPositionInState = (state, taskId) => {
  let oldStatusId;
  let oldTasksIds;
  for (const [statusId, taskIds] of state.get('datasets')) {
    if (!taskIds?.includes(taskId)) {
      continue;
    }

    oldStatusId = statusId;
    oldTasksIds = taskIds.filter((_taskId) => _taskId !== taskId);
  }

  return [oldStatusId, oldTasksIds];
};

const tasksBoardReducer = (state = initialState, action) => {
  switch (action.type) {
    case LOAD_BOARD_TASKS: {
      let value = action.payload.filters.statusId.value;
      if (action.payload.filters.status.value === 'finished') {
        value = 'finished';
      }

      return state.setIn(['pagination', value, 'isLoading'], true).set('projectId', action.extra.projectId ?? null);
    }
    case LOAD_BOARD_TASKS_SUCCESS: {
      const dataset = state.getIn(['datasets', action.statusId]);
      const taskIds = fromJS(action.tasks.items.map((task) => task.taskId));

      let newState = state.setIn(
        ['pagination', action.statusId],
        fromJS({
          totalCount: action.tasks._meta.totalCount,
          pageCount: action.tasks._meta.pageCount,
          currentPage: action.tasks._meta.currentPage,
          perPage: action.tasks._meta.perPage,
          isLoading: false,
        }),
      );

      return newState.setIn(['datasets', action.statusId], dataset ? dataset.merge(taskIds) : taskIds);
    }

    case RESET_BOARD_TASKS: {
      return initialState;
    }

    case UPDATE_TASK_ORDER_SUCCESS: {
      const activeContainer = action.values.initStatusId;
      const overContainer = action.values.statusId;
      const taskId = action.values.taskId;
      const overIndex = action.values.overIndex;
      const isChangedStatusId = activeContainer !== overContainer;
      const overDatasetPath = ['datasets', overContainer];
      let newState = state;
      let overDataset = newState.getIn(overDatasetPath);
      if (!isChangedStatusId) {
        overDataset = overDataset.filter((id) => id !== taskId);
        newState = newState.setIn(overDatasetPath, overDataset);
      }

      const overTotalCountPath = ['pagination', overContainer, 'totalCount'];
      const overTotalCount = newState.getIn(overTotalCountPath);
      if (!overDataset.includes(taskId)) {
        newState = newState
          .setIn(overDatasetPath, overDataset.insert(overIndex, taskId))
          .setIn(overTotalCountPath, overTotalCount + 1);
      }

      if (!isChangedStatusId) {
        return newState;
      }

      const activeDatasetPath = ['datasets', activeContainer];
      const activeTotalCountPath = ['pagination', activeContainer, 'totalCount'];
      const activeTotalCount = newState.getIn(activeTotalCountPath);
      const activeDataset = newState.getIn(activeDatasetPath);

      const activeDatasetFiltered = activeDataset.filter((id) => id !== taskId);
      if (activeDataset.size !== activeDatasetFiltered.size) {
        newState = newState
          .setIn(activeDatasetPath, activeDatasetFiltered)
          .setIn(activeTotalCountPath, activeTotalCount - 1);
      }

      return newState;
    }

    case RESET_TASK_DRAG:
    case UPDATE_TASK: {
      // Данный кейс нужен т.к. сокеты могут ответить быстрее чем
      // придёт ответ от сервера и чтобы мы поменяли положение карточки
      // до изменения её сокетами
      if (typeof action.values?.overIndex === 'undefined') {
        return state;
      }

      const statusId = action.values.statusId;
      const taskId = action.values.taskId;
      let column = statusId;
      if (statusId === null) {
        column = 'empty';
      }

      const pagination = state.getIn(['pagination', column]);
      if (!pagination) {
        for (const [statusId, taskIds] of state.get('datasets')) {
          if (!taskIds?.includes(taskId)) {
            continue;
          }

          const datasetsByStatusId = state.getIn(['datasets', statusId]);
          return state.setIn(
            ['datasets', statusId],
            datasetsByStatusId.filter((_taskId) => _taskId !== taskId),
          );
        }

        return state;
      }

      let dataset = state.getIn(['datasets', column]);
      if (!dataset) {
        dataset = new List();
      }

      if (dataset.includes(taskId)) {
        return state.setIn(['datasets', column], dataset);
      }

      if (action.values.overIndex) {
        dataset = dataset.insert(action.values.overIndex, taskId);
      } else {
        dataset = dataset.unshift(taskId);
      }

      const [oldStatusId, oldTasksIds] = getTaskPositionInState(state, taskId);

      return state
        .setIn(['datasets', oldStatusId], oldTasksIds)
        .setIn(['datasets', column], dataset)
        .setIn(['pagination', column, 'totalCount'], state.getIn(['pagination', column, 'totalCount']) + 1)
        .setIn(['pagination', oldStatusId, 'totalCount'], state.getIn(['pagination', oldStatusId, 'totalCount']) - 1);
    }

    case DELETE_TASK_SUCCESS: {
      let statusColumn;
      for (const [statusId, taskIds] of state.get('datasets')) {
        if (!taskIds?.includes(action.taskId)) {
          continue;
        }

        statusColumn = statusId;
        break;
      }

      let dataset = state.getIn(['datasets', statusColumn]);
      if (!dataset) {
        return state;
      }

      dataset = dataset.filter((id) => id !== action.taskId);

      return state
        .setIn(['datasets', statusColumn], dataset)
        .setIn(['pagination', statusColumn, 'totalCount'], state.getIn(['pagination', statusColumn, 'totalCount']) - 1);
    }

    case APPEND_ACTIVITY_LOG: {
      const { taskId, type, entity, projectId } = action.payload;
      if (entity !== 'Task' || (projectId && projectId !== state.get('projectId'))) {
        return state;
      }

      // Переносим задачу в колонку "закрытые задачи"
      if (type === CLOSE_TYPE) {
        const [oldStatusId, oldTasksIds] = getTaskPositionInState(state, taskId);
        return state
          .setIn(['datasets', oldStatusId], oldTasksIds)
          .setIn(['pagination', oldStatusId, 'totalCount'], state.getIn(['pagination', oldStatusId, 'totalCount']) - 1)
          .setIn(
            ['datasets', TASK_STATUS_FINISHED.get('statusId')],
            state.getIn(['datasets', TASK_STATUS_FINISHED.get('statusId')], fromJS([])).push(taskId),
          );
      }

      if (![UPDATE_TYPE, LABEL_TYPE].includes(type)) {
        return state;
      }

      const { statusId, nextTaskId = null } = action.payload.content.newValues;
      if (!statusId) {
        return state;
      }

      const paginationByNewStatusIdPath = ['pagination', statusId];
      const paginationByNewStatusId = state.getIn(paginationByNewStatusIdPath);
      if (!paginationByNewStatusId) {
        return state;
      }

      const { statusId: oldStatusId } = action.payload.content.oldValues;
      const paginationByOldStatusIdPath = ['pagination', oldStatusId];
      const paginationByOldStatusId = state.getIn(paginationByOldStatusIdPath);
      if (!paginationByOldStatusId) {
        return state;
      }

      let newState = state;

      const fromDatasetPath = ['datasets', oldStatusId];
      const fromDataset = state.getIn(fromDatasetPath, List());
      const fromDatasetFiltered = fromDataset.filter((id) => id !== taskId);
      if (fromDataset.size !== fromDatasetFiltered.size) {
        newState = newState
          .setIn(fromDatasetPath, fromDatasetFiltered)
          .setIn(
            [...paginationByOldStatusIdPath, 'totalCount'],
            state.getIn([...paginationByOldStatusIdPath, 'totalCount'], 1) - 1,
          );
      }

      const toDatasetPath = ['datasets', statusId];
      let toDataset = state.getIn(toDatasetPath, List());
      if (toDataset.includes(taskId)) {
        return newState;
      }

      if (TASK_SPECIAL_STATUS_IDS.includes(statusId)) {
        toDataset = toDataset.push(taskId);
      } else if (nextTaskId === null) {
        toDataset = toDataset.push(taskId);
      } else {
        toDataset = toDataset.insert(toDataset.indexOf(nextTaskId), taskId);
      }

      return newState
        .setIn(toDatasetPath, toDataset)
        .setIn(
          [...paginationByNewStatusIdPath, 'totalCount'],
          state.getIn([...paginationByNewStatusIdPath, 'totalCount'], 0) + 1,
        );
    }

    case UPDATE_TASK_SUCCESS: {
      let newState = state;

      if (action.task.status === 'finished') {
        let finishedDataset = state.getIn(['datasets', 'finished']);
        if (finishedDataset && !finishedDataset.includes(action.task.taskId)) {
          finishedDataset = finishedDataset.push(action.task.taskId);
          const totalCountPath = ['pagination', 'finished', 'totalCount'];
          newState = newState
            .setIn(['datasets', 'finished'], finishedDataset)
            .setIn(totalCountPath, state.getIn(totalCountPath) + 1);
        }

        const datasetPath = ['datasets', action.task.statusId];
        let dataset = newState.getIn(datasetPath);
        if (dataset && dataset.includes(action.task.taskId)) {
          const totalCountPath = ['pagination', action.task.statusId, 'totalCount'];
          newState = newState.setIn(totalCountPath, state.getIn(totalCountPath) - 1).setIn(
            datasetPath,
            newState.getIn(datasetPath).filter((taskId) => action.task.taskId !== taskId),
          );
        }
      }

      return newState;
    }

    default:
      return state;
  }
};

export default tasksBoardReducer;
